import React from 'react'
import { Card, Flex, Tooltip } from 'antd'
import CategorySelect from './CategorySelect'
import { useDispatch, useSelector } from 'react-redux'
import textSubmissionDrawerSelectors from '../../../../../../../../../../store/selectors/text-submission-drawer.selectors'
import assignmentSelectors from '../../../../../../../../../../store/selectors/assignment.selectors'
import { textSubmissionDrawerActions } from '../../../../../../../../../../store/slices/text-submission-drawer.slice'
import ReasoningInput from './ReasoningInput'
import FeedbackInput from './FeedbackInput'
import './grading-card.scss'
import { useFetchQuestionsQuery } from '../../../../../../../../../../store/apis/question.api'
import { isNumber } from 'lodash'
import { fetchQuestionsSelectors } from '../../../../../../../../../../store/apis/utils/selectors/question.api.selectors'
import { useParams } from 'react-router'
import { useVersionId } from '../../../../../../../../../../utils/parseURL.helper'
import { useExplanation } from '../../../../../../../../../../store/apis/utils/hooks/category.hooks'
import { useAnswerContext } from '../../../AnswerContext'
import { useFetchAssignmentSharedWithQuery } from '../../../../../../../../../../store/apis/shared-assignment.api'
import { fetchAssignmentSharedWithSelectors } from '../../../../../../../../../../store/apis/utils/selectors/shared-assignment.api.selectors'
import User from '../../../../../../../../../../components/common/user/User'
import { useAssignmentAccess } from '../../../../../../../access/Access'
import { AssignmentPermissions, Forbiddances } from '../../../../../../../../../../constants/assignment-permissions'
import { useApprovalSpeedAnalytics } from '../../../../../../../../../../hooks/useApprovalSpeedAnalytics'

const GradingCard = ({ className, submissionId, answer, question }) => {
    const isOwner = useSelector(assignmentSelectors.isOwner)
    const ownerId = useSelector(assignmentSelectors.ownerId)
    const ownerName = useSelector(assignmentSelectors.ownerName)
    const dispatch = useDispatch()
    const focusedQuestionId = useSelector(textSubmissionDrawerSelectors.focusedQuestionId)
    const { token } = useParams()
    const versionId = useVersionId()
    const { categoryIdToHighlight, setCategoryIdToHighlight } = useAnswerContext()

    const { data: selectedCategory } = useFetchQuestionsQuery(
        { token, versionId },
        {
            skip: !isNumber(answer.categoryId),
            selectFromResult: result =>
                fetchQuestionsSelectors.category(
                    {
                        categoryId: categoryIdToHighlight,
                    },
                    result,
                ),
        },
    )

    const { data: assignee } = useFetchAssignmentSharedWithQuery(
        { token },
        {
            selectFromResult: result => fetchAssignmentSharedWithSelectors.byUserId({ id: answer.assigneeId }, result),
        },
    )

    const hasReviewingPermissions = useAssignmentAccess(
        [AssignmentPermissions.CAN_REVIEW],
        [
            Forbiddances.IS_MBA,
            Forbiddances.IS_PROCESSING,
            Forbiddances.IS_VERSION_PUBLISHED,
            Forbiddances.VERSION_HAS_MBAS,
        ],
        null,
        answer.assigneeId,
    )

    const showDetails = question.id === focusedQuestionId

    const { explanation } = useExplanation(answer)

    // Analytics: Track approval speed (isolated from business logic)
    const isCurrentlyFocused = focusedQuestionId === question.id
    const calculateApprovalSpeed = useApprovalSpeedAnalytics(answer.id, question.id, submissionId, isCurrentlyFocused)

    const handleClick = () => {
        if (focusedQuestionId === question.id) return
        dispatch(textSubmissionDrawerActions.setFocusedQuestionId(question.id))
        setCategoryIdToHighlight(answer?.categoryId)
    }

    // Determine whose info to display (assignee if available, or owner if it's the owner's assignment)
    const displayAssignee = assignee
        ? { id: assignee.id, name: assignee.name }
        : isOwner && answer.assigneeId === ownerId
          ? { id: ownerId, name: ownerName }
          : null

    return (
        <Card
            size='small'
            title={<span style={{ whiteSpace: 'normal' }}>{question.content}</span>}
            onClick={handleClick}
            className={`${className} grading-card ${focusedQuestionId === question.id && 'focused-grading-card'}`}
            data-id={question.id}
            extra={
                isOwner &&
                displayAssignee && <User size='small' name={displayAssignee.name || '...'} id={displayAssignee.id} />
            }
        >
            <Flex vertical>
                {showDetails && (
                    <ReasoningInput
                        submissionId={submissionId}
                        answerId={answer.id}
                        initialValue={
                            explanation
                                ? explanation.isAdequate
                                    ? explanation.reasoning
                                    : answer.reasoning
                                : answer.isAdequate
                                  ? answer.reasoning || answer.explainabilityReasoning
                                  : answer.reasoning
                        }
                        readonly={!hasReviewingPermissions}
                    />
                )}
                <CategorySelect
                    key={submissionId}
                    submissionId={submissionId}
                    answer={answer}
                    question={question}
                    label={null}
                    showDetails={showDetails}
                    readonly={!hasReviewingPermissions}
                    calculateApprovalSpeed={calculateApprovalSpeed}
                />
                {showDetails && (
                    <FeedbackInput
                        submissionId={submissionId}
                        answerId={answer.id}
                        feedback={answer.feedback || selectedCategory?.feedback || ''}
                        readonly={!hasReviewingPermissions}
                    />
                )}
            </Flex>
        </Card>
    )
}

export default GradingCard
